import { useEffect, useState, useCallback, useMemo, useRef } from "react";

import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { GridColDef } from "@mui/x-data-grid-premium";
import {
  Box,
  Stack,
  CircularProgress,
  Paper,
  Backdrop,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import PatientDatas from "src/modules/medcard/patient-datas";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";

import GlasgowScoreCreate from "../../glasgow-score/create";
import EuroScoreCreate from "../../euro-score/create";
import ByzNurseAddButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseAddButton";
import ByzNursePrintButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard, checkModifyPrivilege } from "src/utils/authorization/OtherPrivilegeGuard";
import { UserOtherPrivilegesEnum } from "src/modules/auth/userOtherPrivilegesSlice";
import authService from "src/api/auth/authService";
import { referenceService } from "src/api/references/referenceService";
import { ConnectionOfMedcardResponseDto } from "src/api/references/referenceServicesDtos";
import { getCardData, CardField } from "src/utils/card/cardData";
import { useUserRole } from "src/hooks/useUserRole";
import { useTranslate } from "src/locales";
import { useDynamicForm } from "src/hooks/useDynamicForm";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import { MedicalCardLoadingProvider, useMedicalCardLoading } from "src/hooks/useMedicalCardLoading";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";

import CasusScoreCreate from "../../casus-score/create";
import ModifyAldetreCreate from "../../modify-aldetre-score/create";
import AcuteGvhdClinicalScoringCreate from "../../acute-ghvd-clinical-scoring/create";
import EdineburghDeptScaleCreate from "../../edineburgh-dept-scale/create";
import CapriniRiskAssessmentCreate from "../../caprini-risk-assessment/create";
import SnapIIScoreCreate from "../../snap2-score/pages/create";
import PediatricTraumaScoreCreate from "../../pediatric-trauma-score/create";
import SofaScoreCreate from "../../sofa-score/create";
import PainAssesmentScaleScoreCreate from "../../pain-assesment-scale/create";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import CardFooter from "./CardFooter";
import ImproveBloodRiskCreate from "../../improve-blood-risk/create";
import ImproveVTERiskAssessmentCreate from "../../improve-vte-risk-assessment/create";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { getLocalISO, formatDateTime, dateTimeFormatter, calculateAge } from "src/utils/date/dateHelper";
import PrismScoreCreate from "../../prism-score/create";
import Saps2ScoreCreate from "../../saps2-score/pages/create";
import CanScaleCreate from "../../can-scale/create";
import NeomodScoreCreate from "../../neomod-score/create";
import VitalSignsCreate from "../../vital-signs/create";
import FallRiskHarizmiCreate from "../../fall-risk-harizmi/create";
import FallRiskItakiCreate from "../../fall-risk-itaki/pages/create";
import FluidBalanceCreate from "../../fluid-balance/create";
import VteRiskFactorEvaluationCreate from "../../vte-risk-faktor-evaluation/create";
import BasiBraden from "../../basi-braden/create";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";
import NewbornSkinRiskAssessment from "../../newborn-skin-risk-assessment/create";

function GlobalCardLoader() {
  const { isLoading } = useMedicalCardLoading();
  const { t } = useTranslate();

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        borderRadius: "8px",
      }}
    >
      <CircularProgress size={50} thickness={4} />
      <ByzMuiTypography variant="h6" color="primary">
        {t("loading")}
      </ByzMuiTypography>
    </Box>
  );
}

function MedCardModalContent({
  children,
  saving,
  t,
}: {
  children: React.ReactNode;
  saving: boolean;
  t: any;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <GlobalCardLoader />
      {saving && (
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.modal + 11,
            position: "absolute",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "8px",
          }}
          open={saving}
        >
          <CircularProgress color="inherit" size={60} thickness={4} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, textAlign: "center", color: "#fff" }}
          >
            {t("card.saving-medical-card")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.9, textAlign: "center", color: "#fff" }}
          >
            {t("card.please-wait")}
          </Typography>
        </Backdrop>
      )}
      {children}
    </Box>
  );
}

function CardList() {
  const CustomToolbar = () => (
    <ByzDataGridToolbar containerSx={{ pl: 0 }} toolbarSx={{ pl: 0 }} />
  );
  const theme = useTheme();
  const isCompactScreen = useMediaQuery(theme.breakpoints.down("lg"));

  type CardRow = {
    title: string;
    value: string;
    headerName?: string;
    type?: string;
  };

  const [currentUrlState, setCurrentUrlState] = useState(
    window.location.pathname + window.location.search
  );

  const [cardListAlertOpen, setCardListAlertOpen] = useState(false);
  const [cardListAlertMessage, setCardListAlertMessage] = useState("");
  const [cardListAlertSeverity, setCardListAlertSeverity] = useState<
    "error" | "success"
  >("success");

  const currentUrl = currentUrlState;
  const urlParams = new URLSearchParams(window.location.search);
  
  const mainTabs = useAppSelector((state: any) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector(
    (state: any) => state.tabs.activeTabIndex
  );
  const activeTab = mainTabs[activeTabIndex];
  const ptId = activeTab?.data?.patientId as number;

  const orIdFromUrl = Number(urlParams.get("orId")) || 0;
  const orIdFromData = activeTab?.data?.orId ? Number(activeTab.data.orId) : 0;
  const orId = orIdFromUrl || orIdFromData;

  useEffect(() => {
    if (!orId && ptId) {
      console.warn('CardList: orId missing!', {
        url: window.location.href,
        ptId,
        activeTab: activeTab,
        orIdFromUrl,
        orIdFromData
      });
    }
  }, [orId, ptId, activeTab, orIdFromUrl, orIdFromData]);

  // URL path'ından cardName çıkar: /medcard/improve-vte-risk-assessment/123 -> IMPROVE_VTE_RISK_ASSESSMENT
  const getCardNameFromPath = (pathname: string): string => {
    const parts = pathname.split('/');
    const medcardIndex = parts.findIndex(p => p === 'medcard');
    if (medcardIndex >= 0 && parts[medcardIndex + 1]) {
      const cardPath = parts[medcardIndex + 1];
      // improve-vte-risk-assessment -> IMPROVE_VTE_RISK_ASSESSMENT
      return cardPath.toUpperCase().replace(/-/g, '_');
    }
    return '';
  };


  const { t } = useTranslate();
  const { user } = useAppSelector((state: any) => state.auth);
  const { getOtherPrivilege } = useUserRole();
  const cardPrivilege = getOtherPrivilege(UserOtherPrivilegesEnum.MedicalCards, orId);

  const [open, setOpen] = useState(false);
  // Duplicate declarations removed

  const [rows, setRows] = useState<any[]>([]);
  const [comId, setComId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [externalPatientData, setExternalPatientData] = useState<any>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const savingRef = useRef<boolean>(false);
  const savingResetTimeoutRef = useRef<number | null>(null);
  const [sameBranchMap, setSameBranchMap] = useState<Record<string, boolean>>({});
  const lastFillTableKeyRef = useRef<string | null>(null);

  // Determine Card Name based on logic (e.g. Age for Basi Braden)
  const getDerivedCardName = (baseName: string, patientData: any) => {
    if (baseName === "BASI_BRADEN_RISK_SCORE" || baseName === "BASI_BRADEN") {
      if (patientData?.data?.birthdate) {
        const age = calculateAge(patientData.data.birthdate);
        if (age) {
          if (age.totalDays <= 28) {
            return "BASI_BRADEN_RISK_SCORE_BBQ";
          } else if (age.years < 6) {
            return "BASI_BRADEN_RISK_SCORE_Q";
          }
        }
      }
      return "BASI_BRADEN_RISK_SCORE";
    }
    return baseName;
  };

  const baseCardName = urlParams.get("cardName") || getCardNameFromPath(window.location.pathname);
  const cardName = getDerivedCardName(baseCardName, externalPatientData);
  const CARD_DATA = getCardData(cardName);

  const { formData, handleChange, toColumns, setFormData } = useDynamicForm<any>(
    CARD_DATA?.formData || {}
  );

  useEffect(() => {
    if (CARD_DATA?.formData) {
      setFormData(CARD_DATA.formData);
    }
  }, [CARD_DATA, setFormData]);

  useEffect(() => {
    setComId(null);
  }, [ptId, orId]);

  const showAlert = useCallback((message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'success') => {
    setCardListAlertMessage(message);
    setCardListAlertSeverity(severity === "error" ? "error" : "success");
    setCardListAlertOpen(true);
  }, []);

  // Edit state
  const [editItemId, setEditItemId] = useState<number | null>(null);

  const isReadOnly = useMemo(() => {
    if (!cardPrivilege) return true;
    if (cardPrivilege.modifypriv === '3') return false;

    if (editItemId) {
      const row = rows.find(r => r.id === editItemId);
      if (row) {
        const owner = row.cuser || row.CUSER;
        const canWrite = checkModifyPrivilege('1', cardPrivilege, user?.username || null, owner, sameBranchMap[owner] || false);
        return !canWrite;
      }
    } else {
      // Create mode - check if user has create permission (modifyPriv 1, 2, or 3)
      const priv = cardPrivilege.modifypriv;
      if (priv === '1' || priv === '2' || priv === '3') return false;
    }
    return true;
  }, [cardPrivilege, editItemId, rows, user, sameBranchMap]);

  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setEditItemId(null);
  }, []);
  useEffect(() => {
    const fetchBranchChecks = async () => {

      if (cardPrivilege?.modifypriv !== '2') return;

      if (!rows || rows.length === 0) return;

      const usernames = Array.from(new Set(rows.map(r => r.cuser || r.CUSER).filter(u => !!u)));
      if (usernames.length === 0) return;

      try {
        const response = await authService.checkUserBranches(usernames);
        if (response.data) {
          setSameBranchMap(response.data);
        }
      } catch (error) {
        console.error("Error checking user branches", error);
      }
    };

    fetchBranchChecks();
  }, [rows, cardPrivilege]);

  const columns: GridColDef[] = useMemo(() => {
    if (!CARD_DATA?.formData) return [];

    return [
      {
        field: "update",
        headerName: t("card.update"),
        width: 150,
        align: "center",
        renderCell(params: any) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
              <OtherPrivilegeGuard
                uopmid={UserOtherPrivilegesEnum.MedicalCards}
                privid={orId}
                owner={params.row.cuser || params.row.CUSER}
                extraAccess={sameBranchMap[params.row.cuser || params.row.CUSER] || false}
                fallback={<ByzGrdEditButton disabled />}
              >
                <ByzGrdEditButton
                  onClick={() => {
                    setEditItemId(params.row.id);
                    setOpen(true);
                  }}
                />
              </OtherPrivilegeGuard>
            </Box>
          );
        },
      },
      ...(CARD_DATA.fields || []).map((field: CardField) => {
        const column: GridColDef = {
          ...field,
          type: field.type as any,
          headerName: t(field.headerName),
          renderCell: field.computed && field.calculate
            ? (params: any) => {
              if (!params || !params.row) return "0";
              const result = field.calculate!(params.row);
              return result.toString();
            }
            : undefined,
        };

        // Tarih/saat alanları için valueFormatter ekle
        if (field.type === "date" || field.type === "Date") {
          column.valueFormatter = (value: any) => {
            if (!value) return "";
            return dateTimeFormatter(value, "dd.MM.yyyy HH:mm");
          };
        }

        return column;
      }),
      {
        field: "delete",
        headerName: t("card.delete"),
        flex: 1,
        align: "center",
        renderCell: (params: any) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <OtherPrivilegeGuard
              uopmid={UserOtherPrivilegesEnum.MedicalCards}
              privid={orId}
              modifyPriv="1"
              owner={params.row.cuser || params.row.CUSER}
              extraAccess={sameBranchMap[params.row.cuser || params.row.CUSER] || false}
              fallback={<ByzGrdDeleteButton disabled />}
            >
              <ByzGrdDeleteButton
                onClick={() => handleDeleteClick(params.row.id)}
                disabled={deleteLoading === params.row.id}
                loading={deleteLoading === params.row.id}
              />
            </OtherPrivilegeGuard>
          </Box>
        ),
      },
    ];
  }, [CARD_DATA?.fields, CARD_DATA?.formData, t, deleteLoading, user, sameBranchMap]);

  const fillTable = useCallback(
    async (cardDataParam?: any, ptIdParam?: any, orIdParam?: number) => {
      const cardData = cardDataParam ?? CARD_DATA;
      const pt = ptIdParam ?? ptId;
      const or = orIdParam ?? orId ?? 0;

      if (!cardData) return;

      // PATIENT_MONITORING ve VITAL_SIGNS kendi özel list component'ini kullanıyor
      if (cardName === "PATIENT_MONITORING" || cardName === "VITAL_SIGNS") {
        return null;
      }

      let columnValue = 0;

      if (cardData.columnValue === 0) {
        const response =
          (await referenceService.connectionsOfMedcard.getConnectionOfMedCard({
            pt,
            orId: or,
          })) as ConnectionOfMedcardResponseDto;

        if (response.data && response.data.length > 0) {
          const sorted = response.data.sort((a, b) => b.comid - a.comid);
          const _comId = sorted[0].comid;

          const masterResponse = await referenceService.card.getCardTableData({
            tableName: cardData.masterTableName,
            columnName: "COM_ID",
            columnValue: _comId,
          });

          if (masterResponse.data && masterResponse.data.length > 0) {
            columnValue = masterResponse.data[0].ID;
          } else {
            columnValue = 0;
          }
        } else {
          columnValue = 0;
        }
      } else {
        columnValue = cardData.columnValue;
      }

      try {
        const mcpdNameList = Object.keys(cardData.mcpConfig || {}).join(",");
        if (columnValue) {
          const response = await referenceService.card.getCardTableData({
            tableName: cardData.tableName,
            columnName: cardData.columnName,
            columnValue,
            mcpdNameList,
          });

          if (response.data?.length > 0) {
            const formattedRows = response.data.map(
              (item: any, index: number) => {
                const row: any = { ...item };
                cardData.rows.forEach(
                  ({ title, value, type }: CardRow) => {
                    if (type === "date" || type === "Date") {
                      row[title] = formatDateTime(item[value], "dd.MM.yyyy HH:mm");
                    } else if (type === "checkbox" || type === "Checkbox") {
                      row[title] = item[value] ? "Evet" : "Hayır";
                    } else {
                      row[title] = item[value];
                    }
                  }
                );
                row.id = item.id ?? item.ID ?? `row-${index}`;
                return row;
              }
            );

            setRows(formattedRows);
          } else {
            setRows([]);
          }
        } else {
          setRows([]);
        }
      } catch (error: any) {
        console.error("getCardTableData error:", error);
      }
    },
    [CARD_DATA, ptId, orId]
  );

  const getCardTableData = useCallback(async (comId: number): Promise<string | ""> => {
    try {
      if (!CARD_DATA) return "";
      const response = await referenceService.card.getCardTableData({
        tableName: CARD_DATA.masterTableName,
        columnName: "COM_ID",
        columnValue: comId,
      });

      if (response.data && response.data.length > 0) {
        const first = response.data[0] as any;
        // Hem Master hem de Detail ID isimlerini destekle
        const existingId = first.ID ?? first.id ?? first.Id ??
          (CARD_DATA.columnName ? (first[CARD_DATA.columnName] ?? first[CARD_DATA.columnName.toUpperCase()]) : null);
        return existingId ? existingId.toString() : "";
      }

      return "";
    } catch (error: any) {
      console.error("getCardTableData error:", error);
      return "";
    }
  }, [CARD_DATA]);

  type FormattedRow = Record<string, any>;

  const getCardTableDataWithTableId = useCallback(async (
    id: number
  ): Promise<FormattedRow[]> => {
    try {
      if (!CARD_DATA) return [];
      const mcpdNameList = Object.keys(CARD_DATA.mcpConfig || {}).join(",");

      const response = await referenceService.card.getCardTableData({
        tableName: CARD_DATA.tableName,
        columnName: "ID",
        columnValue: id,
        mcpdNameList,
      });

      const formattedRows: FormattedRow[] = (response.data ?? []).map(
        (item: any, index: number) => {
          const row: FormattedRow = {};

          CARD_DATA.rows.forEach(({ title, value, type }: CardRow) => {
            if (type === "date") {
              const dateValue = item[value] ? new Date(item[value]) : null;
              row[title] = dateValue
                ? new Intl.DateTimeFormat("tr-TR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false,
                }).format(dateValue)
                : null;
            } else {
              row[title] = item[value] ?? null;
            }
          });

          row.id = item.id ?? item.ID ?? `row-${index}`;
          return row;
        }
      );

      return formattedRows;
    } catch (error) {
      console.error("getCardTableData error:", error);
      return [];
    }
  }, [CARD_DATA]);

  const getComId = useCallback(async (): Promise<number | null> => {
    // Mevcut bir comId varsa ve ptId/orId değişmemişse (aslında değişmişse useEffect ile comId null'lanır)
    // onu kullan, aksi takdirde API'ye git.
    if (comId) return comId;

    try {
      if (!ptId) return null;

      const response =
        (await referenceService.connectionsOfMedcard.getConnectionOfMedCard({
          pt: ptId,
          orId: orId || 0,
        })) as ConnectionOfMedcardResponseDto;

      const sorted = response.data.sort((a, b) => b.comid - a.comid);
      let _comId = sorted[0]?.comid;

      if (!_comId) {
        const createResponse =
          await referenceService.connectionsOfMedcard.createConnectionOfMedCard(
            {
              pt_id: ptId,
              or_id: orId || 0,
            }
          );

        if (createResponse?.comid) {
          _comId = createResponse.comid;
        } else if (createResponse?.data !== undefined) {
          if (typeof createResponse.data === "number") {
            _comId = createResponse.data;
          } else if (
            Array.isArray(createResponse.data) &&
            createResponse.data.length > 0
          ) {
            const sortedCreate = createResponse.data.sort(
              (a, b) => b.comid - a.comid
            );
            _comId = sortedCreate[0]?.comid;
          } else {
            const retryResponse =
              (await referenceService.connectionsOfMedcard.getConnectionOfMedCard(
                {
                  pt: ptId,
                  orId: orId || 0,
                }
              )) as ConnectionOfMedcardResponseDto;

            const retrySorted = retryResponse.data.sort(
              (a, b) => b.comid - a.comid
            );
            _comId = retrySorted[0]?.comid;
          }
        } else {
          const retryResponse =
            (await referenceService.connectionsOfMedcard.getConnectionOfMedCard(
              {
                pt: ptId,
                orId: orId || 0,
              }
            )) as ConnectionOfMedcardResponseDto;

          const retrySorted = retryResponse.data.sort(
            (a, b) => b.comid - a.comid
          );
          _comId = retrySorted[0]?.comid;
        }
      }

      if (!_comId) {
        setCardListAlertMessage(t("card.connection-id-not-found"));
        setCardListAlertSeverity("error");
        setCardListAlertOpen(true);
        return null;
      }

      setComId(_comId);
      return _comId;
    } catch (error) {
      console.error("getComId error:", error);
      setCardListAlertMessage(t("card.get-connection-error"));
      setCardListAlertSeverity("error");
      setCardListAlertOpen(true);
      return null;
    }
  }, [ptId, orId, t, comId]);

  const saveDetails = useCallback(async (cardId: string, itemId?: string, pain_scale?: number) => {
    if (!cardId || !CARD_DATA) return;

    const allowedFields = Object.keys(CARD_DATA.formData);

    // Ana kolon (Master ID kolonu) her zaman dahil edilmeli, 
    // toColumns'dan gelse de gelmese de biz manuel ekleyeceğiz.
    const filteredColumns = toColumns().filter((col) =>
      allowedFields.includes(col.key) && col.key !== CARD_DATA.columnName
    );

    const updatedColumns = [
      ...filteredColumns,
      { key: CARD_DATA.columnName, value: cardId }
    ];

    if (pain_scale && pain_scale > 0) {
      const now = getLocalISO();

      const newColumns = [
        { key: CARD_DATA.columnName, value: cardId },
        { key: "CTIME", value: now },
        { key: "PROCESS_TIME", value: now },
        { key: "CUSER", value: user?.username ?? "" },
      ];

      return await referenceService.card.post({
        tablename: CARD_DATA.tableName,
        columns: newColumns,
      });
    }

    if (editItemId && itemId) {
      return await referenceService.card.put({
        tablename: CARD_DATA.tableName,
        id: editItemId,
        columns: updatedColumns,
      });
    }

    return await referenceService.card.post({
      tablename: CARD_DATA.tableName,
      columns: updatedColumns,
    });
  }, [CARD_DATA, toColumns, editItemId, user]);

  const createTableMasterId = useCallback(async (
    comIdParam?: number
  ): Promise<string | null> => {
    const _comId = comIdParam ?? comId;
    if (!_comId || !CARD_DATA) {
      return null;
    }
    try {
      const response = await referenceService.card.post({
        tablename: CARD_DATA.masterTableName,
        columns: [{ key: "COM_ID", value: _comId.toString() }],
      });

      const responseAny = response as any;

      if (!responseAny) return null;

      // Farklı API cevap formatlarını destekle (ID, id, Id, data.ID, vb.)
      const getMasterId = (obj: any): string | null => {
        if (!obj) return null;
        if (obj.ID) return obj.ID.toString();
        if (obj.id) return obj.id.toString();
        if (obj.Id) return obj.Id.toString();

        // Bazı tablolarda ID alanı tablonun kendi ID kolonu adıyla gelir (örn: PSRB_ID)
        if (CARD_DATA.columnName && obj[CARD_DATA.columnName]) {
          return obj[CARD_DATA.columnName].toString();
        }

        if (obj.data) {
          if (typeof obj.data === "object") {
            return getMasterId(obj.data);
          }
          if (typeof obj.data === "string" || typeof obj.data === "number") {
            return obj.data.toString();
          }
        }
        return null;
      };

      const masterId = getMasterId(responseAny);
      if (masterId) return masterId;

      return null;
    } catch (error) {
      console.error("createTableMasterId error:", error);
      return null;
    }
  }, [comId, CARD_DATA]);

  const save = useCallback(async (id?: string): Promise<any> => {
    if (savingRef.current) return null; // Çift tıklamayı engelle
    let closeAfterSave = false;
    let saveResult = null;
    try {
      if (!CARD_DATA) return null;
      savingRef.current = true;
      setSaving(true);
      const _comId = await getComId();
      if (!_comId) {
        savingRef.current = false;
        setSaving(false);
        return null;
      }
      setComId(_comId);

      let cardDetailId: string | null = null;
      const isCreateMode = !editItemId || !id || id === "";

      // Master-Detail kartlarda masterId'yi (Parent ID) doğru tespit etmemiz lazım
      if (!isCreateMode) {
        // GÜNCELLEME MODU
        if (CARD_DATA.masterTableName && CARD_DATA.columnName) {
          // Önce grid satırından bak (en güvenilir yer, veritabanından ilk gelen halidir)
          const editingRow = rows.find((r) => String(r.id) === String(id) || String(r.ID) === String(id));
          const masterIdFromRow = editingRow ? (editingRow[CARD_DATA.columnName] ?? editingRow[CARD_DATA.columnName.toUpperCase()] ?? editingRow[CARD_DATA.columnName.toLowerCase()]) : null;

          // Satırda yoksa form datasına bak
          const masterIdFromForm = formData[CARD_DATA.columnName] ?? formData[CARD_DATA.columnName.toUpperCase()] ?? formData[CARD_DATA.columnName.toLowerCase()];

          cardDetailId = (masterIdFromRow ?? masterIdFromForm ?? null)?.toString();
        } else {
          // Master-Detail değilse detay ID'si ile devam et
          cardDetailId = id;
        }
      } else {
        // KAYIT EKLEME MODU
        cardDetailId = await getCardTableData(_comId);

        if (!cardDetailId) {
          // Mevcut master kayıt yoksa yeni oluştur
          cardDetailId = await createTableMasterId(_comId);
        }
      }

      if (!cardDetailId) {
        const errorMsg = `${CARD_DATA.columnName} (Master ID) bulunamadı.`;
        console.warn(errorMsg);
        showAlert(errorMsg, "error");
        savingRef.current = false;
        setSaving(false);
        return null;
      }

      let pain_scale = 0;
      if (CARD_DATA.tableName === "MC_PAIN_ASSMENT_SCORE_DET") {
        pain_scale = 1;
      }

      // İlişki kolonunu (NSRAS_ID vb.) Master ID ile güncelle
      handleChange(CARD_DATA.columnName, cardDetailId);

      const responsePost = await saveDetails(cardDetailId, id, pain_scale);

      if (responsePost?.issuccess) {
        saveResult = responsePost;
        showAlert(editItemId ? t("card.record-updated-successfully") : t("card.record-added-successfully"), "success");
        await fillTable();

        // Modalın kapanmasını sağlayan state güncellemeleri
        setOpen(false);
        setEditItemId(null);
      } else {
        showAlert(editItemId ? t("card.update-failed") : t("card.add-failed"), "error");
      }
    } catch (err) {
      console.error("save error:", err);
      showAlert(t("card.save-error"), "error");
    } finally {
      if (closeAfterSave) {
        if (savingResetTimeoutRef.current) {
          window.clearTimeout(savingResetTimeoutRef.current);
        }
        savingResetTimeoutRef.current = window.setTimeout(() => {
          savingRef.current = false;
          setSaving(false);
          savingResetTimeoutRef.current = null;
        }, 500);
      } else {
        savingRef.current = false;
        setSaving(false);
      }
    }
    return saveResult;
  }, [getComId, getCardTableData, createTableMasterId, handleChange, saveDetails, editItemId, fillTable, t, CARD_DATA, showAlert, rows, formData]);

  const handleDeleteClick = useCallback(async (id: number) => {
    setDeleteItemId(id);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!CARD_DATA || !deleteItemId) return false;

    try {
      setDeleteLoading(deleteItemId);
      await referenceService.card.delete({
        tablename: CARD_DATA.tableName,
        id: deleteItemId,
      });

      await fillTable();

      setCardListAlertOpen(true);
      setCardListAlertSeverity("success");
      setCardListAlertMessage(t("card.record-deleted-successfully"));
      setDeleteDialogOpen(false);
      setDeleteItemId(null);
      return true;
    } catch (error) {
      console.error("Delete error:", error);
      setCardListAlertOpen(true);
      setCardListAlertSeverity("error");
      setCardListAlertMessage(t("card.delete-failed"));
      return false;
    } finally {
      setDeleteLoading(null);
    }
  }, [CARD_DATA, deleteItemId, fillTable, t]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteItemId(null);
    setDeleteLoading(null);
  }, []);

  useEffect(() => {
    if (CARD_DATA && ptId) {
      if (!cardPrivilege && orId) return; // Only check privilege if orId is present? Or maybe cardPrivilege is required?
      
      // If orId is 0, cardPrivilege might be undefined/null.
      // We should check if we really need it.
      // If we assume non-orId pages are readable by anyone with access to patient...
      
      const key = `${cardName}-${ptId}-${orId || 0}`;
      if (lastFillTableKeyRef.current === key) return;
      lastFillTableKeyRef.current = key;
      fillTable(CARD_DATA, ptId, orId || 0);
    }
  }, [fillTable, CARD_DATA, ptId, orId, cardPrivilege]);

  if (!CARD_DATA) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div>
          {t("card.card-data-not-found")}: {cardName}
        </div>
        <div>URL: {currentUrl}</div>
        <div>
          Tarayıcı URL: {window.location.pathname + window.location.search}
        </div>
      </Box>
    );
  }

  if (!CARD_DATA.formData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div>{t("card.form-data-not-found")}</div>
      </Box>
    );
  }



  if (cardName === "PAIN_ASSESSMENT_SCALE") {
    return (
      <PainAssesmentScaleScoreCreate
        recordId={editItemId?.toString()}
        patientData={externalPatientData}
        patientId={ptId}
        onClose={() => {
          setOpen(false);
          setEditItemId(null);
          fillTable();
        }}
        setEditItemId={setEditItemId}
        getComId={getComId}
        comId={comId ?? 0}
        setComId={setComId}
        getCardTableData={getCardTableData}
        getCardTableDataWithTableId={getCardTableDataWithTableId}
        createTableMasterId={createTableMasterId}
        handleChange={handleChange}
        save={save}
        handleDeleteClick={handleDeleteClick}
        handleDeleteConfirm={handleDeleteConfirm}
        handleDeleteCancel={handleDeleteCancel}
        setDeleteLoading={setDeleteLoading}
        deleteLoading={deleteLoading}
        rows={rows}
        setRows={setRows}
        setDeleteItemId={setDeleteItemId}
        extraAccess={!isReadOnly}
      />
    );
  }

  if (cardName == "FLUID_BALANCE") {
    return (
      <FluidBalanceCreate
        comId={comId}
        getComId={getComId}
        ptId={ptId}
        orId={orId}
      />
    );
  }

  const renderCreateComponent = () => {
    const commonProps = {
      recordId: editItemId?.toString(),
      patientId: ptId,
      onClose: () => {
        setOpen(false);
        setEditItemId(null);
        fillTable();
      },
      getComId,
      comId: comId ?? 0,
      setComId,
      getCardTableData,
      createTableMasterId,
      handleChange,
      save,
      isSaving: saving,
      extraAccess: !isReadOnly,
    };

    if (cardName.startsWith("BASI_BRADEN_RISK_SCORE") || cardName === "BASI_BRADEN") {
      return (
        <BasiBraden
          {...commonProps}
          cardType={cardName}
          patientData={externalPatientData}
        />
      );
    }

    switch (cardName) {
      case "GLASGOW_SCORE":
        return <GlasgowScoreCreate {...commonProps} patientData={externalPatientData} />;
      case "EURO_SCORE":
        return <EuroScoreCreate {...commonProps} patientData={externalPatientData} />;
      case "CASUS_SCORE":
        return <CasusScoreCreate {...commonProps} />;
      case "ALDETRE_SCORE":
        return <ModifyAldetreCreate {...commonProps} />;
      case "ACUTE_GHVD_CLINICAL_SCORING":
        return <AcuteGvhdClinicalScoringCreate {...commonProps} />;
      case "EDINEBURGH_DEPT_SCALE":
        return <EdineburghDeptScaleCreate {...commonProps} />;
      case "CAPRINI_RISK_ASSESSMENT":
        return <CapriniRiskAssessmentCreate {...commonProps} patientData={externalPatientData} />;
      case "PEDIATRIC_TRAUMA_SCORE":
        return <PediatricTraumaScoreCreate {...commonProps} />;
      case "SNAP_II_SCORE":
        return <SnapIIScoreCreate {...commonProps} />;
      case "IMPROVE_BLEEDING_RISK":
        return <ImproveBloodRiskCreate {...commonProps} patientData={externalPatientData} />;
      case "IMPROVE_VTE_RISK_ASSESSMENT":
        return <ImproveVTERiskAssessmentCreate {...commonProps} patientData={externalPatientData} />;
      case "PRISM_SCORE":
        return <PrismScoreCreate {...commonProps} patientData={externalPatientData} showAlert={showAlert} />;
      case "SOFA_SCORE":
        return <SofaScoreCreate {...commonProps} patientData={externalPatientData} />;
      case "SAPS2_SCORE":
        return <Saps2ScoreCreate {...commonProps} patientData={externalPatientData} showAlert={showAlert} />;
      case "CAN_SCALE":
        return <CanScaleCreate {...commonProps} patientData={externalPatientData} />;
      case "NEOMOD_SCORE":
        return <NeomodScoreCreate {...commonProps} patientData={externalPatientData} />;
      case "VITAL_SIGNS":
        return (
          <VitalSignsCreate
            recordId={commonProps.recordId}
            patientId={commonProps.patientId}
            patientData={externalPatientData}
            onClose={commonProps.onClose}
            getComId={getComId}
            comId={commonProps.comId}
            setComId={setComId}
            getCardTableData={getCardTableData}
            createTableMasterId={createTableMasterId}
            handleChange={handleChange}
            save={save}
            extraAccess={commonProps.extraAccess}
          />
        );
      case "FALL_RISK_HARIZMI":
        return <FallRiskHarizmiCreate {...commonProps} patientData={externalPatientData} />;
      case "FALL_RISK_ITAKI":
        return <FallRiskItakiCreate {...commonProps} patientData={externalPatientData} />;
      case "VTE_RISK_FACTOR_EVALUATION":
        return <VteRiskFactorEvaluationCreate {...commonProps} patientData={externalPatientData} />;
      case "NEWBORN_SKIN_RISK_ASSESSMENT":
        return (
          <NewbornSkinRiskAssessment
            recordId={commonProps.recordId}
            patientId={commonProps.patientId}
            onClose={commonProps.onClose}
            handleChange={handleChange}
            save={save}
            isSaving={saving}
          />
        );
      default:
        return <Box>{t("card.no-creation-component")}</Box>;
    }
  };

  const responsiveColumns = columns.map((column) => {
        if (!isCompactScreen) return column;

        const headerText = String(column.headerName ?? "");
        const normalizedField = String(column.field ?? "").toLowerCase();
        const normalizedHeader = headerText.toLowerCase();
        const isActionColumn =
          normalizedField.includes("edit") ||
          normalizedField.includes("delete") ||
          normalizedField.includes("update") ||
          normalizedField.includes("action") ||
          normalizedHeader.includes("düzenle") ||
          normalizedHeader.includes("duzenle") ||
          normalizedHeader.includes("güncelle") ||
          normalizedHeader.includes("guncelle") ||
          normalizedHeader.includes("sil");

        const targetWidth = isActionColumn
          ? 120
          : headerText.length >= 16
            ? 180
            : headerText.length >= 10
              ? 160
              : 140;

        return {
          ...column,
          flex: undefined,
          minWidth: Math.max(column.minWidth ?? 0, targetWidth),
          width: Math.max(column.width ?? 0, targetWidth),
        };
      });

  return (
    <OtherPrivilegeGuard
      uopmid={UserOtherPrivilegesEnum.MedicalCards}
      privid={orId}
      fallback={
        <Box sx={{ p: 3, textAlign: "center" }}>
          <div>{t("no-permission-for-this-action")}</div>
        </Box>
      }
    >
      <PatientDatas
        isOpenMedCard={true}
        patientDataOut={setExternalPatientData}
        cardRows={rows}
        cardData={CARD_DATA}
        patientId={ptId}
        orId={orId}
      />

      <ByzMuiSnackBar
        open={cardListAlertOpen}
        type={cardListAlertSeverity}
        title={cardListAlertSeverity === "error" ? "Hata" : "Bilgi"}
        message={cardListAlertMessage}
        onClose={() => setCardListAlertOpen(false)}
      />

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        patientId={deleteItemId?.toString() || ""}
        title={t("card.delete-confirmation")}
        content={t("card.delete-confirmation-message")}
        onConfirm={handleDeleteConfirm}
      />

      <Stack mt={5} direction="row" spacing={2}>
        <OtherPrivilegeGuard
          uopmid={UserOtherPrivilegesEnum.MedicalCards}
          privid={orId}
          modifyPriv="1"
          fallback={
            <ByzNurseAddButton
              disabled={true}
              onClick={() => { }}
            />
          }
        >
          <ByzNurseAddButton
            onClick={() => {
              setEditItemId(null);
              setOpen(true);
            }}
          />
        </OtherPrivilegeGuard>
        <ByzNursePrintButton disabled />
        <OpenComponentDialog
          title={
            editItemId
              ? `${t(CARD_DATA.title)} ${t("card.edit")}`
              : t(CARD_DATA.title)
          }
          open={open}
          onClose={handleCloseDialog}
          isMedCard={true}
        >
          <MedicalCardLoadingProvider>
            <MedCardModalContent saving={saving} t={t}>
              {renderCreateComponent()}
            </MedCardModalContent>
          </MedicalCardLoadingProvider>
        </OpenComponentDialog>
      </Stack>

      <Box sx={{ minHeight: 400 }}>
        <Paper
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            pb: { xs: 2, sm: 3, md: 4 },
            my: 3,
            borderRadius: "16px",
            overflowX: "auto",
          }}
        >
          <ByzMuiDataGrid
            sx={{
              mb: 0,
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: 1.25,
              },
            }}
            customToolbar={CustomToolbar}
            columns={responsiveColumns}
            pageSize={10}
            rows={rows}
            whiteGrid={true}
          />

          <CardFooter />

        </Paper>
      </Box>
    </OtherPrivilegeGuard >
  );
}

export default CardList;
